/**
 * Main's continuous-glucose feed (`/data/getContinuousGlucose`), rebuilt from the
 * port's measurements.
 *
 * The Continual tab (ContinuousGraph) is main's weekly diary of a FreeStyle
 * LibreLink sensor and asks twice:
 *
 *  - `{}` -> `{pageCount}`: how many pages of seven days the chart holds;
 *  - `{pageInfo: {page}}` -> `{data, scannedData}`: one page (0 is the newest
 *    week), `data` the sensor's readings averaged to 15 minutes and
 *    `scannedData` the scans, each as `{date: 'MM/DD/YYYY', time: 'HH:MM',
 *    'Historic Glucose mg/dL' | 'Scan Glucose mg/dL': text, day}`.
 *
 * Main kept both in `frontendcache2` (`DbUtility::getContinuousGlucoseData`),
 * built from the LibreLink source's readings under the locally-minted
 * continuous code (BUCMUL001) and the glucose LOINC code (41653-7) for a scan.
 * The port's entry asked for one device's raw readings by an id the page never
 * sends (`/patients/{p}/devices/null/readings`), which the bridge short-circuited
 * to an empty list, so the shape's `item.data.length` threw and the tab drew
 * nothing. The same two series are in the chart's measurements, and this builds
 * main's two answers from them.
 */

import { listAllMeasurements } from './vitalsBridge';

/** The importer the series come from (`data_sources.code`; the measurements list takes it as `filter[source]`). */
export const GLUCOSE_SOURCE = 'freestyle_librelink';

/** The 15-minute sensor series: a locally-minted code, because no LOINC code names a CGM sample. */
export const CONTINUOUS_CODE = 'BUCMUL001';

/** A scan of the sensor, and the glucose of a finger stick: LOINC glucose, mass/volume in blood. */
export const SCAN_CODE = '41653-7';

/** Main's page: seven days. */
export const DAYS_PER_PAGE = 7;

const DAY_MS = 86400000;

function pad(n) {
  return String(n).padStart(2, '0');
}

/** The reading's time where the viewer is, as main's `timestamp without time zone` read back. */
function localInstant(measuredAt) {
  const when = new Date(measuredAt);

  if (!measuredAt || Number.isNaN(when.getTime())) return null;

  const year = when.getFullYear();
  const month = when.getMonth() + 1;
  const dayOfMonth = when.getDate();

  return {
    date: `${pad(month)}/${pad(dayOfMonth)}/${year}`,
    // Whole days since 1970 of the calendar date, for differences between days.
    dayNumber: Math.round(Date.UTC(year, month - 1, dayOfMonth) / DAY_MS),
    hours: when.getHours(),
    minutes: when.getMinutes(),
    // Sorts the same as the instant.
    stamp: `${year}${pad(month)}${pad(dayOfMonth)}${pad(when.getHours())}${pad(when.getMinutes())}`,
  };
}

/** The number a reading carries, or null for text ("HI") and blanks. */
function numberOf(row) {
  const raw = row.value_numeric !== null && row.value_numeric !== undefined && String(row.value_numeric).trim() !== ''
    ? row.value_numeric
    : row.value;
  const parsed = raw === null || raw === undefined || String(raw).trim() === '' ? NaN : Number(raw);

  return Number.isFinite(parsed) ? parsed : null;
}

/** avg(...)::text, to the two places the graph can tell apart. */
function averageText(values) {
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;

  return String(Math.round(mean * 100) / 100);
}

/**
 * The two series from the LibreLink readings: the sensor's readings averaged to
 * a quarter hour, and the scans as they were taken. Newest first, as main's
 * `ORDER BY ts DESC`.
 *
 * @return {{averages: Array, scans: Array, days: Array<number>}} `days` are the
 *         distinct calendar days either series has, newest first
 */
export function glucoseSeries(rows) {
  const buckets = new Map();
  const scans = [];

  (rows || []).forEach((row) => {
    const at = localInstant(row && row.measured_at);
    const value = row ? numberOf(row) : null;

    if (!at || value === null) return;

    if (row.code === CONTINUOUS_CODE) {
      const quarter = at.minutes - (at.minutes % 15);
      const key = `${at.date}|${pad(at.hours)}:${pad(quarter)}`;
      const bucket = buckets.get(key) || {
        date: at.date, dayNumber: at.dayNumber, time: `${pad(at.hours)}:${pad(quarter)}`,
        stamp: `${at.stamp.slice(0, 8)}${pad(at.hours)}${pad(quarter)}`, values: [],
      };

      bucket.values.push(value);
      buckets.set(key, bucket);
    } else if (row.code === SCAN_CODE) {
      scans.push({
        date: at.date, dayNumber: at.dayNumber, time: `${pad(at.hours)}:${pad(at.minutes)}`, stamp: at.stamp, value,
      });
    }
  });

  const averages = [...buckets.values()]
    .map((bucket) => ({ ...bucket, value: averageText(bucket.values) }))
    .sort((a, b) => (a.stamp < b.stamp ? 1 : -1));
  scans.sort((a, b) => (a.stamp < b.stamp ? 1 : -1));

  const days = [...new Set([...averages, ...scans].map((entry) => entry.dayNumber))].sort((a, b) => b - a);

  return { averages, scans, days };
}

/** Main's page count: weeks of seven days, the last one part full. */
export function glucosePageCount(series) {
  return Math.floor((series.days.length + DAYS_PER_PAGE - 1) / DAYS_PER_PAGE);
}

/**
 * One page, as main shaped it. `day` counts days back from the newest day the
 * series has (a scan's, modulo seven, as main's second query computed it); the
 * graph groups a day's readings on it.
 */
export function glucosePage(series, page) {
  const pageDays = new Set(series.days.slice(page * DAYS_PER_PAGE, (page + 1) * DAYS_PER_PAGE));
  const newestAverage = series.averages.length ? series.averages[0].dayNumber : 0;
  const newestScan = series.scans.length ? series.scans[0].dayNumber : 0;

  return {
    data: series.averages
      .filter((entry) => pageDays.has(entry.dayNumber))
      .map((entry) => ({
        date: entry.date,
        time: entry.time,
        'Historic Glucose mg/dL': entry.value,
        day: newestAverage - entry.dayNumber,
      })),
    scannedData: series.scans
      .filter((entry) => pageDays.has(entry.dayNumber))
      .map((entry) => ({
        date: entry.date,
        time: entry.time,
        'Scan Glucose mg/dL': String(entry.value),
        day: (newestScan - entry.dayNumber) % DAYS_PER_PAGE,
      })),
  };
}

/**
 * `/data/getContinuousGlucose` for a chart. No chart in view is no readings:
 * zero pages, or an empty page (the graph returns early on `data.length === 0`
 * where main's `{error}` for a page that is not there would have thrown).
 *
 * @param {number|null} patientId  the chart in view
 * @param {object} payload         `{}` for the page count, `{pageInfo: {page}}` for a page
 */
export async function readContinuousGlucose(patientId, payload = {}, deps = {}) {
  const pageInfo = payload && payload.pageInfo;
  const empty = pageInfo === undefined || pageInfo === null || pageInfo === false;

  if (patientId === null || patientId === undefined || patientId === '') {
    return empty ? { pageCount: 0 } : { data: [], scannedData: [] };
  }

  const rows = await listAllMeasurements(patientId, { 'filter[source]': GLUCOSE_SOURCE }, deps);
  const series = glucoseSeries(rows);

  if (empty) return { pageCount: glucosePageCount(series) };

  const page = Number(pageInfo.page);

  if (!Number.isInteger(page) || page < 0 || page >= glucosePageCount(series)) return { data: [], scannedData: [] };

  return glucosePage(series, page);
}

export default { readContinuousGlucose, glucoseSeries, glucosePageCount, glucosePage };
